import { useMemo, useEffect, useRef } from "react";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import { Button, Grid } from "@mui/material";
import { useDispatch } from "react-redux";
import { CustomFieldsV2 } from "./CustomFieldsV2";
import _ from "lodash";
import { PolicyResource, IFullFlight, IGroundEvent } from "@vmosolutions/oc-interfaces";
import { getFormFieldDiff } from "src/utils";
import { FormikFormIsDirtyEffect, IFormHandle } from "src/pages/TimelineEventEditors/helpers";

// API calls
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";

interface ICustomEventProps {
  windowId: string;
  customValues: any; // the custom field values from the flight object
  eventId: string; // flight id
  customSchema: string; // the schema to validate the field values
  onSubmit?: (values: any) => void;
  updateTabState?: (dirtyStatus: boolean) => void; // update parent with dirty status.
  setLoadedEvent: (values: IFullFlight | IGroundEvent) => void;
  isFlight: boolean;
  registerFormHandle?: (tabId: string, handle: IFormHandle | null) => void;
  saveAllDirtyTabs?: (excludeTabId?: string) => Promise<void>;
  formTabId?: string;
}

type CustomForm = FormikProps<any>;

export const CustomEvent = (props: ICustomEventProps): JSX.Element => {
  const { 
      windowId,
      customValues,
      onSubmit,
      eventId,
      customSchema,
      updateTabState,
      setLoadedEvent,
      isFlight,
      registerFormHandle,
      saveAllDirtyTabs,
      formTabId 
  } = props;

  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<Record<string, unknown>>>(null);

  // Register form handle so parent can trigger this tab's submit for save-all
  useEffect(() => {
    if (!formTabId) return undefined;
    registerFormHandle?.(formTabId, {
      submitForm: () => formikRef.current?.submitForm() ?? Promise.resolve(),
      isDirty: () => formikRef.current?.dirty ?? false,
    });
    return () => { registerFormHandle?.(formTabId, null); };
  }, [registerFormHandle, formTabId]);

  const mosaicPane = useMemo(() => {
    return document.getElementById(`${windowId}-panel`);
  }, [windowId]);

  const schema = useMemo(() => (customSchema ? JSON.parse(customSchema) : null), [customSchema]);
  // console.log("schema", schema);

  const initialValues = useMemo(() => {
    const values = { ...customValues };
    if (!schema) {
      return values;
    }
    Object.keys(schema.properties).forEach((value: string) => {
      values[value] =
        (customValues && customValues[value]) ??
        schema.properties[value].default ??
        schema.properties[value]?.selectorProps?.defaultValue;
    });
    return values;
  }, [customValues, schema]);

  const handleSubmit = async (values: any, helpers: FormikHelpers<any>): Promise<void> => {
    // Since each tab only modified a partial section of custom code, only include those modified by the form in the payload.
    const dirtyFields = getFormFieldDiff(values, initialValues);

    const { setSubmitting, setStatus, resetForm } = helpers;
    setSubmitting(true);
    if (onSubmit) {
      onSubmit(values);
    } else {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Updating Custom Fields...",
        successMessage: "Custom Fields Successfully Updated",
        errorMessage: "Error: Unable to Update Custom Fields",
      };

      const response = await AmplifyAPIWrapper.Put<any>(
        `/${isFlight ? "flights" : "groundEvents"}/${eventId}`,
        { custom: dirtyFields },
        notifications
      );
      if (isFlight && response?.newEvent?._id) {
        // update loaded flight in flight editor
        setLoadedEvent(response?.newEvent); // Re-initializes form.
      } else if (!isFlight && response._id) {
        setLoadedEvent(response);
      }
    }
    setSubmitting(false);

    // Trigger save on all other dirty tabs
    await saveAllDirtyTabs?.(formTabId)
      ?.catch((error) => console.error("Failed to save all dirty tabs:", error));
  };

  if (!schema) {
    return <span>loading...</span>;
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        innerRef={formikRef}
        onReset={() => {
          updateTabState(false);
        }}
      >
        {(customForm: CustomForm) => {
          const { isValid, dirty, isSubmitting } = customForm;

          return (
            <Form>
              <FormikFormIsDirtyEffect updateParent={updateTabState} formDirtyState={dirty} />
              <Grid container justifyContent="flex-start" alignItems="center">
                <CustomFieldsV2
                  mosaicPane={mosaicPane}
                  schema={schema}
                  resource={isFlight ? PolicyResource.Flight : PolicyResource.GroundEvent}
                  labelWidth={150}
                />
                <Grid container style={{ marginLeft: 10 }}>
                  <Button
                    style={{ fontSize: 12, margin: 5, padding: "2px 8px" }}
                    type="reset"
                    disabled={!dirty}
                    variant="contained"
                    color="secondary"
                  >
                    Reset
                  </Button>
                  <Button
                    style={{ fontSize: 12, margin: 5, padding: "2px 8px" }}
                    type="submit"
                    disabled={!isValid || !dirty || isSubmitting}
                    variant="contained"
                    color="primary"
                  >
                    Update
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
